/* eslint-disable react/forbid-prop-types */
import React, { useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import {
  FormHelperText,
  Grid,
  Radio as MuiRadio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material'
import { InputLabel } from '@material-ui/core'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import radioStyles from './styles'
import generalInputStyles from '../styles'

/**
 * Renders a Radio component with a label, options, and validation rules.
 * @param {Object} props - The props object.
 * @param {Object} props.menuProps - The props for the menu.
 * @param {Array} props.options - The array of options to be displayed in the radio.
 * @param {string} props.label - The label for the radio.
 * @param {string} props.name - The name of the radio. (Hook-Form related)
 * @param {boolean} [props.required=false] - Whether the radio is required or not.
 * @param {Function} props.getValue - The function to get the value of an option. (MenuItem related)
 * @param {Function} [props.getLabel=()=>{}] - The function to get the label of an option. (MenuItem related)
 * @param {Function} props.getKey - The function to get the key of an option. (MenuItem related. Each option must have a unique key)
 * @param {string} [props.defaultValue=''] - The default value of the radio.
 * @param {boolean} [props.disabled=false] - Whether the radio is disabled or not.
 * @param {Object} [props.style={}] - The style object for the radio container.
 * @param {string} [props.containerClasses=null] - The classes for the radio container.
 * @param {string} [props.labelClasses=null] - The classes for the radio label.
 * @param {string} [props.radioClasses=null] - The classes for the Mui radio component.
 * @returns {React.ReactElement} - The Radio component.
 */
function Radio({
  label,
  name,
  required = false,
  defaultValue = '',
  disabled = false,
  containerClasses,
  labelClasses,
  radioClasses,
  radioGroupClasses,
  labelRadio1,
  labelRadio2,
  valueRadio1,
  valueRadio2,
  radioOptionLabel = { fontSize: '14px', fontWeight: '500' },
}) {
  const generalStyles = generalInputStyles()
  const localStyles = radioStyles({ radioOptionLabel })
  const { control, errors, setValue } = useFormContext()

  useEffect(() => {
    setValue(name, defaultValue)
  }, [defaultValue, name, setValue])

  return (
    <Grid className={classNames(localStyles.column, containerClasses ?? null)}>
      {label && (
        <InputLabel
          variant="standard"
          id={`${name}-label`}
          style={{ color: errors[name] ? '#C90000' : null, top: '-38px' }}
          className={classNames(generalStyles.inputLabel, labelClasses ?? null)}
        >
          {label}
          {required && <span className={generalStyles.required}>*</span>}
        </InputLabel>
      )}

      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue ?? ''}
        rules={{ required: { value: required, message: 'Esse campo é obrigatório' } }}
        as={
          <RadioGroup
            required={required}
            disabled={disabled}
            error={!!errors[name]}
            className={classNames(localStyles.radioGroup, radioGroupClasses ?? null)}
          >
            <FormControlLabel
              value={valueRadio1 ?? 'true'}
              label={labelRadio1 ?? 'Sim'}
              control={<MuiRadio />}
              disabled={disabled}
              className={classNames(localStyles.inputRadio, radioClasses ?? null)}
            />
            <FormControlLabel
              value={valueRadio2 ?? 'false'}
              label={labelRadio2 ?? 'Não'}
              control={<MuiRadio />}
              disabled={disabled}
              className={classNames(localStyles.inputRadio, radioClasses ?? null)}
            />
          </RadioGroup>
        }
      />
      <FormHelperText className={generalStyles.errorMessage} error={!!errors[name]}>
        {errors[name]?.message}
      </FormHelperText>
    </Grid>
  )
}

Radio.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  containerClasses: PropTypes.string,
  labelClasses: PropTypes.string,
  radioClasses: PropTypes.string,

  radioGroupClasses: PropTypes.string,
  labelRadio1: PropTypes.string,
  labelRadio2: PropTypes.string,
  valueRadio1: PropTypes.string,
  valueRadio2: PropTypes.string,
}

Radio.defaultProps = {
  required: false,
  defaultValue: '',
  disabled: false,
  containerClasses: null,
  labelClasses: null,
  radioClasses: null,

  radioGroupClasses: null,
  labelRadio1: null,
  labelRadio2: null,
  valueRadio1: null,
  valueRadio2: null,
}

export default Radio
